// src/components/page_components/PersonalInformationForm.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  Popper,
  Box,
  Divider,
  Chip,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { useUserQuery } from "../../../../hooks/api_hooks/useUserQuery";
import { useQueryClient } from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";

function BelowPopper(props) {
  return <Popper {...props} placement="bottom-start" />;
}

/* ─── Shared field styles ─── */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#F8FAFC",
    fontSize: "0.9rem",
    transition: "box-shadow 0.2s ease, background-color 0.2s ease",
    "&:hover fieldset": { borderColor: "#09b2f0" },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 3px rgba(9,178,240,0.12)",
    },
    "&.Mui-focused fieldset": { borderColor: "#09b2f0", borderWidth: "1.5px" },
    "&.Mui-disabled": { backgroundColor: "#F1F5F9" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#09b2f0" },
  "& .MuiFormHelperText-root": { fontSize: "0.75rem", mt: "4px" },
};

/* ─── Section header ─── */
function SectionLabel({ icon: Icon, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Icon sx={{ fontSize: 15, color: "#09b2f0" }} />
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#94A3B8",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* ─── Read-only display cell ─── */
function ReadonlyField({ icon, label, value, borderLeft, borderTop, span }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        borderLeft: borderLeft ? "1px solid #F1F5F9" : "none",
        borderTop: borderTop ? "1px solid #F1F5F9" : "none",
        gridColumn: span ? `span ${span}` : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.4 }}>
        {icon}
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#CBD5E1",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1E293B" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function PersonalInformationForm() {
  const { user } = useAuth();
  const { data } = useUserQuery();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [error] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const empty = useMemo(
    () => ({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      birthday: "",
      streetAddress: "",
      unitNumber: "",
      city: "",
      state: "",
      zipCode: "",
    }),
    [],
  );

  const userRecord = data?.user;
  const personalInfo = userRecord?.personal_information ?? empty;

  const [initialData, setInitialData] = useState(empty);
  const [formData, setFormData] = useState(empty);
  const firstLoad = useRef(true);
  const hasHydrated = useRef(false);

  useEffect(() => {
    if (userRecord) firstLoad.current = false;
  }, [userRecord]);

  useEffect(() => {
    if (userRecord && !hasHydrated.current) {
      const norm = normalizeIncoming(personalInfo, empty);
      setInitialData(norm);
      setFormData(norm);
      if (Object.values(norm).every((v) => !v)) setEditing(true);
      hasHydrated.current = true;
    }
  }, [userRecord, personalInfo, empty]);

  const isDirty = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(initialData),
    [formData, initialData],
  );

  /* ─── Save ─── */
  const onSave = async (e) => {
    e?.preventDefault?.();
    const trimmed = trimAll(formData);
    const validation = validateAll(trimmed);
    setFieldErrors(validation.errors);
    if (!validation.ok) return;

    setSaving(true);
    const previousUser = queryClient.getQueryData(["user"]);

    try {
      queryClient.setQueryData(["user"], (old) => {
        if (!old?.user) return old;
        return { ...old, user: { ...old.user, personal_information: trimmed } };
      });

      const res = await fetch(
        "https://api.kloudscan.com/personal-information",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${user?.access_token ?? ""}`,
          },
          body: JSON.stringify(trimmed),
        },
      );

      if (!res.ok) throw new Error(`Failed to save: ${res.status}`);

      const saved = safeParse(await res.text());
      const next = normalizeIncoming(saved, trimmed);

      setInitialData(next);
      setFormData(next);
      setEditing(false);

      queryClient.setQueryData(["user"], (old) => {
        if (!old?.user) return old;
        return { ...old, user: { ...old.user, personal_information: next } };
      });

      await queryClient.invalidateQueries({
        queryKey: ["user"],
        refetchType: "inactive",
      });

      window.scrollTo({ top: 0, behavior: "smooth" });

      setSnack({
        open: true,
        message: "Changes saved successfully",
        severity: "success",
      });

      const isAuthorizationSigned = !!userRecord?.authorization_signed;
      setTimeout(() => {
        if (!isAuthorizationSigned)
          navigate("/profile/authorization", { replace: true });
      }, 1000);
    } catch (err) {
      console.error("Save failed:", err);
      if (previousUser) queryClient.setQueryData(["user"], previousUser);
      setSnack({
        open: true,
        message: "Save failed — changes reverted",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  /* ─── Handlers ─── */
  const onChange = (field) => (e) => {
    const val = e.target.value;
    setFormData((p) => ({ ...p, [field]: val }));
    if (fieldErrors[field]) setFieldErrors((f) => ({ ...f, [field]: "" }));
  };

  const onPhoneBlur = () => {
    const normalized = normalizePhone(formData.phoneNumber);
    const valid = isValidNanp(normalized);
    setFormData((p) => ({ ...p, phoneNumber: normalized }));
    if (!valid && normalized)
      setFieldErrors((f) => ({ ...f, phoneNumber: "Invalid phone number" }));
  };

  const onEdit = () => setEditing(true);
  const onCancel = () => {
    setFormData(initialData);
    setFieldErrors({});
    setEditing(false);
  };

  /* ─── Loading state ─── */
  if (!hasHydrated.current) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#09b2f0" }} />
      </Box>
    );
  }

  return (
    <>
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
            fontWeight: 600,
            fontSize: "0.875rem",
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* ── Card Header ── */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FAFBFD",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: "1rem", fontWeight: 700, color: "#0F172A" }}
            >
              Personal Information
            </Typography>
            <Typography
              sx={{ fontSize: "0.78rem", color: "#94A3B8", mt: 0.25 }}
            >
              Your name, contact, and address details
            </Typography>
          </Box>

          {!editing ? (
            <Button
              onClick={onEdit}
              startIcon={<EditIcon sx={{ fontSize: "15px !important" }} />}
              size="small"
              sx={{
                backgroundColor: "#f59e0b",
                color: "#000",
                fontWeight: 700,
                fontSize: "0.78rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  opacity: 0.85,
                  boxShadow: "none",
                },
              }}
            >
              Edit
            </Button>
          ) : (
            <Chip
              label="Editing"
              size="small"
              sx={{
                backgroundColor: "#FFF7ED",
                color: "#EA580C",
                fontWeight: 600,
                fontSize: "0.72rem",
                border: "1px solid #FED7AA",
                borderRadius: "6px",
                height: 26,
              }}
            />
          )}
        </Box>

        {/* ── Read-only Summary (collapsed view) ── */}
        {!editing && (
          <Box sx={{ px: 3, py: 2.5 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 0,
                borderRadius: "10px",
                border: "1px solid #F1F5F9",
                overflow: "hidden",
              }}
            >
              <ReadonlyField
                icon={<PersonIcon sx={{ fontSize: 13, color: "#09b2f0" }} />}
                label="Name"
                value={
                  [formData.firstName, formData.lastName]
                    .filter(Boolean)
                    .join(" ") || "—"
                }
              />
              <ReadonlyField
                icon={
                  <ContactPhoneIcon sx={{ fontSize: 13, color: "#09b2f0" }} />
                }
                label="Phone"
                value={formData.phoneNumber || "—"}
                borderLeft
              />
              <ReadonlyField
                icon={<PersonIcon sx={{ fontSize: 13, color: "#09b2f0" }} />}
                label="Date of Birth"
                value={
                  formData.birthday
                    ? dayjs(formData.birthday).format("MMM D, YYYY")
                    : "—"
                }
                borderLeft
              />
              <ReadonlyField
                icon={<HomeIcon sx={{ fontSize: 13, color: "#09b2f0" }} />}
                label="Address"
                value={
                  formData.streetAddress
                    ? [
                        formData.streetAddress,
                        formData.unitNumber,
                        formData.city,
                        formData.state,
                        formData.zipCode,
                      ]
                        .filter(Boolean)
                        .join(", ")
                    : "—"
                }
                borderTop
                span={3}
              />
            </Box>
          </Box>
        )}

        {/* ── Edit Form (expanded view) ── */}
        {editing && (
          <Box sx={{ px: 3, py: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
                {error}
              </Alert>
            )}

            <Stack spacing={3}>
              {/* Personal Details */}
              <Box>
                <SectionLabel icon={PersonIcon} label="Personal Details" />
                <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <TextField
                      required
                      label="First Name"
                      value={formData.firstName}
                      onChange={onChange("firstName")}
                      error={!!fieldErrors.firstName}
                      helperText={fieldErrors.firstName}
                      fullWidth
                      sx={fieldSx}
                    />
                    <TextField
                      required
                      label="Last Name"
                      value={formData.lastName}
                      onChange={onChange("lastName")}
                      error={!!fieldErrors.lastName}
                      helperText={fieldErrors.lastName}
                      fullWidth
                      sx={fieldSx}
                    />
                  </Box>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Date of Birth"
                      value={
                        formData.birthday ? dayjs(formData.birthday) : null
                      }
                      onChange={(v) =>
                        setFormData((p) => ({
                          ...p,
                          birthday: v ? v.format("YYYY-MM-DD") : "",
                        }))
                      }
                      slotProps={{
                        textField: {
                          required: true,
                          fullWidth: true,
                          error: !!fieldErrors.birthday,
                          helperText: fieldErrors.birthday,
                          sx: fieldSx,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Box>

              {/* Contact */}
              <Box>
                <SectionLabel icon={ContactPhoneIcon} label="Contact" />
                <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />
                <TextField
                  required
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={onChange("phoneNumber")}
                  onBlur={onPhoneBlur}
                  error={!!fieldErrors.phoneNumber}
                  helperText={
                    fieldErrors.phoneNumber || "Format: (###) ###-####"
                  }
                  fullWidth
                  sx={fieldSx}
                />
              </Box>

              {/* Address */}
              <Box>
                <SectionLabel icon={HomeIcon} label="Address" />
                <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr",
                      gap: 2,
                    }}
                  >
                    <TextField
                      required
                      label="Street Address"
                      value={formData.streetAddress}
                      onChange={onChange("streetAddress")}
                      error={!!fieldErrors.streetAddress}
                      helperText={fieldErrors.streetAddress}
                      fullWidth
                      sx={fieldSx}
                    />
                    <TextField
                      label="Unit / Apt #"
                      value={formData.unitNumber}
                      onChange={onChange("unitNumber")}
                      error={!!fieldErrors.unitNumber}
                      helperText={fieldErrors.unitNumber}
                      fullWidth
                      sx={fieldSx}
                    />
                  </Box>

                  <TextField
                    required
                    label="City"
                    value={formData.city}
                    onChange={onChange("city")}
                    error={!!fieldErrors.city}
                    helperText={fieldErrors.city}
                    fullWidth
                    sx={fieldSx}
                  />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Autocomplete
                      options={US_STATES.map((s) => `${s.code} — ${s.name}`)}
                      value={
                        US_STATES.find(
                          (opt) =>
                            opt.code === formData.state ||
                            opt.name === formData.state,
                        )
                          ? `${formData.state} — ${
                              US_STATES.find(
                                (opt) => opt.code === formData.state,
                              )?.name
                            }`
                          : formData.state || ""
                      }
                      onChange={(e, val) => {
                        const code = val ? val.split("—")[0].trim() : "";
                        setFormData((p) => ({ ...p, state: code }));
                        if (fieldErrors.state)
                          setFieldErrors((f) => ({ ...f, state: "" }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="State"
                          error={!!fieldErrors.state}
                          helperText={fieldErrors.state}
                          sx={fieldSx}
                        />
                      )}
                      freeSolo
                      PopperComponent={BelowPopper}
                    />

                    <TextField
                      required
                      label="ZIP Code"
                      value={formData.zipCode}
                      onChange={onChange("zipCode")}
                      error={!!fieldErrors.zipCode}
                      helperText={fieldErrors.zipCode}
                      fullWidth
                      sx={fieldSx}
                    />
                  </Box>
                </Stack>
              </Box>
            </Stack>

            {/* ── Action Buttons ── */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 3,
                pt: 2.5,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              <Button
                onClick={onCancel}
                startIcon={<CloseIcon sx={{ fontSize: "15px !important" }} />}
                sx={{
                  color: "#64748B",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2,
                  border: "1px solid #E2E8F0",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#F8FAFC",
                    borderColor: "#CBD5E1",
                    boxShadow: "none",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={!isDirty || saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={14} sx={{ color: "#fff" }} />
                  ) : (
                    <SaveIcon sx={{ fontSize: "15px !important" }} />
                  )
                }
                sx={{
                  backgroundColor: saving || !isDirty ? "#E2E8F0" : "#f59e0b",
                  color: saving || !isDirty ? "#94A3B8" : "#000",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: saving || !isDirty ? "#E2E8F0" : "#f59e0b",
                    opacity: saving || !isDirty ? 1 : 0.85,
                    boxShadow: "none",
                  },
                }}
              >
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

/* ─── US States ─── */
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "AS", name: "American Samoa" },
  { code: "GU", name: "Guam" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "PR", name: "Puerto Rico" },
  { code: "VI", name: "U.S. Virgin Islands" },
  { code: "UM", name: "U.S. Minor Outlying Islands" },
];

/* ─── Helpers ─── */
function normalizeIncoming(payload, fallback) {
  if (!payload) return { ...fallback };
  return Object.keys(fallback).reduce((acc, key) => {
    acc[key] =
      typeof payload[key] === "string"
        ? sanitize(payload[key])
        : (payload[key] ?? fallback[key]);
    return acc;
  }, {});
}

function sanitize(str) {
  if (typeof str !== "string") return str;
  return str.replace(/[;><{}$]/g, "").slice(0, 100);
}

function trimAll(obj) {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    out[k] = typeof v === "string" ? sanitize(v.trim()) : v;
  });
  return out;
}

function validateAll(data) {
  const errors = {};
  const required = [
    "firstName",
    "lastName",
    "phoneNumber",
    "birthday",
    "streetAddress",
    "city",
    "state",
    "zipCode",
  ];
  for (const f of required) if (!data[f]) errors[f] = "Required";
  if (data.phoneNumber && !isValidNanp(data.phoneNumber))
    errors.phoneNumber = "Invalid phone number";
  return { ok: Object.keys(errors).length === 0, errors };
}

function digitsOnly(s) {
  return (s || "").replace(/\D+/g, "");
}

function normalizePhone(input) {
  const d = digitsOnly(input);
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return input;
}

function isValidNanp(s) {
  const d = digitsOnly(s);
  return d.length === 10 || (d.length === 11 && d.startsWith("1"));
}

function safeParse(t) {
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}
